import axios from 'axios';
import type { TenantErrorCode } from '@/types/tenant';

const getApiBaseUrl = (): string => {
  if (import.meta.env.VITE_API_BASE_URL) {
    const u = import.meta.env.VITE_API_BASE_URL as string;
    const trimmed = u.replace(/\/+$/, '');
    if (trimmed.endsWith('/api/v1')) return trimmed;
    if (trimmed.endsWith('/api')) return `${trimmed}/v1`;
    return `${trimmed}/api/v1`;
  }
  return '/api/v1';
};

const apiBaseUrl = getApiBaseUrl();

/**
 * Resolve subdomain to tenant UUID.
 * 1. Try GET /api/v1/tenants/resolve?subdomain=<slug> (no X-Tenant-ID required).
 * 2. Fall back to VITE_TENANT_IDS JSON map { "demo": "uuid", ... }.
 * 3. Fall back to VITE_TENANT_ID (single tenant for all subdomains).
 */
export async function resolveSubdomainToTenantId(subdomain: string): Promise<string | null> {
  const slug = subdomain.toLowerCase().trim();
  if (!slug) return null;

  // 1. Optional backend resolve endpoint (no tenant header)
  try {
    const { data } = await axios.get<{ id: string }>(`${apiBaseUrl}/tenants/resolve`, {
      params: { subdomain: slug },
      timeout: 10000,
    });
    if (data?.id && typeof data.id === 'string') {
      return data.id.trim();
    }
  } catch {
    // 404 or no endpoint: fall through to env
  }

  // 2. Env map: VITE_TENANT_IDS='{"demo":"uuid","cat":"uuid"}'
  const idsJson = import.meta.env.VITE_TENANT_IDS as string | undefined;
  if (idsJson) {
    try {
      const map = JSON.parse(idsJson) as Record<string, string>;
      const id = map[slug];
      if (id && typeof id === 'string') return id.trim();
    } catch {
      // ignore
    }
  }

  // 3. Single tenant env: VITE_TENANT_ID=uuid
  const singleId = import.meta.env.VITE_TENANT_ID as string | undefined;
  if (singleId && typeof singleId === 'string') {
    return singleId.trim();
  }

  return null;
}

/**
 * Validate tenant with backend by calling an endpoint that requires X-Tenant-ID.
 * Uses a direct axios call (no app http client) to avoid circular dependency.
 * 200 or 401 => tenant valid; 400/403 with tenant error => invalid.
 */
export async function validateTenant(tenantId: string): Promise<{
  valid: boolean;
  errorCode: TenantErrorCode | null;
}> {
  try {
    await axios.get(`${apiBaseUrl}/users/me`, {
      headers: { 'X-Tenant-ID': tenantId },
      timeout: 10000,
      validateStatus: (status) => status === 200 || status === 401,
    });
    return { valid: true, errorCode: null };
  } catch (err: unknown) {
    const response = (err as { response?: { status: number; data?: { error?: string } } })
      ?.response;
    const status = response?.status ?? 0;
    const body = response?.data;
    const code = getTenantErrorCodeFromResponse(status, body);
    return { valid: false, errorCode: code ?? 'tenant_config' };
  }
}

/** Backend tenant error messages (single `error` field). */
const TENANT_NOT_FOUND = 'Tenant not found';
const MISSING_HEADER = 'Missing required header: X-Tenant-ID';
const TENANT_INACTIVE = 'Tenant inactive';
const SUBSCRIPTION_EXPIRED = 'Subscription expired';

export function getTenantErrorCodeFromResponse(
  status: number,
  body: { error?: string } | undefined
): TenantErrorCode | null {
  const msg = body?.error?.trim();
  if (!msg) return null;
  if (status === 400 && (msg === TENANT_NOT_FOUND || msg === MISSING_HEADER)) {
    return 'tenant_not_found';
  }
  if (status === 403 && msg === TENANT_INACTIVE) return 'tenant_inactive';
  if (status === 403 && msg === SUBSCRIPTION_EXPIRED) return 'subscription_expired';
  return null;
}
